import React from 'react';
import { Link } from 'react-router-dom';
import { EXPLORE_SECTIONS } from '../mock';
import { useReveal } from '../hooks/useReveal';

function TreesIcon({ size = 84 }) {
  const pine = 'M0 -34 L7 -22 L3.5 -22 L11 -9 L6 -9 L15 6 L8 6 L18 22 L-18 22 L-8 6 L-15 6 L-6 -9 L-11 -9 L-3.5 -22 L-7 -22 Z';
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" fill="none" aria-hidden="true">
      <circle cx="60" cy="60" r="58" fill="#4a9cd4" />
      <defs>
        <clipPath id="intro-ring">
          <circle cx="60" cy="60" r="58" />
        </clipPath>
      </defs>
      <g clipPath="url(#intro-ring)">
        <g transform="translate(38 84) scale(0.62)">
          <path d={pine} fill="#fff" />
          <rect x="-3" y="22" width="6" height="12" fill="#fff" />
        </g>
        <g transform="translate(60 86) scale(1)">
          <path d={pine} fill="#fff" />
          <rect x="-3.2" y="22" width="6.4" height="14" fill="#fff" />
        </g>
        <g transform="translate(82 84) scale(0.72)">
          <path d={pine} fill="#fff" />
          <rect x="-3" y="22" width="6" height="12" fill="#fff" />
        </g>
        <path d="M-6 108 Q60 84 126 108 L126 130 L-6 130 Z" fill="#fff" />
      </g>
    </svg>
  );
}

function Intro() {
  const [ref, inView] = useReveal();
  return (
    <section className="relative bg-[#f8f6f2] py-24 md:py-32 overflow-hidden">
      <span className="watermark absolute -top-6 left-1/2 -translate-x-1/2 text-[140px] md:text-[240px] whitespace-nowrap">
        Explore
      </span>
      <div ref={ref} className={`relative max-w-[900px] mx-auto px-6 text-center reveal ${inView ? 'in-view' : ''}`}>
        <div className="flex justify-center mb-8">
          <TreesIcon />
        </div>
        <h2 className="font-serif-ah text-[38px] md:text-[54px] leading-tight">
          <span className="text-[#3d7ca6]">explore</span> <span className="text-[#2f2f2f]">the possibilities.</span>
        </h2>
        <p className="font-serif-ah text-[#3a3a3a] text-[22px] md:text-[26px] font-300 leading-relaxed mt-8">
          The Almaguin Highlands is a place where many choose to spend their cherished vacation time, make their small
          business dreams a reality and make better lives for their families. On behalf of the Almaguin Highlands Chamber
          of Commerce, we would like to invite you to experience this great place we call home.
        </p>
      </div>
    </section>
  );
}

function ExploreBlock({ data }) {
  const [ref, inView] = useReveal();
  const imgRight = data.imageSide === 'right';
  return (
    <div ref={ref} className={`grid md:grid-cols-2 min-h-[600px] reveal ${inView ? 'in-view' : ''}`}>
      {/* text panel */}
      <div className={`bg-[#e9e2d8] flex items-center ${imgRight ? 'md:order-1' : 'md:order-2'}`}>
        <div className="px-8 md:px-20 py-16 max-w-[560px]">
          <p className="font-sans-ah tracking-nav text-[12px] text-[#2f2f2f] font-500 mb-6">{data.tag}</p>
          <h3 className="font-serif-ah text-[42px] md:text-[52px] leading-none mb-6">
            <span className="text-[#3d7ca6] italic">{data.titleAccent}</span>{' '}
            <span className="text-[#2f2f2f]">{data.titleRest}</span>
          </h3>
          <p className="font-sans-ah text-[#454545] text-[16px] font-300 leading-relaxed mb-6">{data.body}</p>
          <p className="font-sans-ah tracking-wide-ah text-[12px] text-[#2f2f2f] font-500 uppercase mb-8">{data.note}</p>
          <div className="flex flex-wrap gap-4">
            {data.buttons.map((b) => (
              <Link
                key={b.label}
                to={b.href}
                className={`font-sans-ah text-[13px] px-8 py-3.5 rounded-sm ${
                  b.style === 'solid' ? 'btn-ah-solid' : 'btn-ah-outline-dark'
                }`}
              >
                {b.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
      {/* image panel */}
      <div className={`relative overflow-hidden min-h-[380px] group ${imgRight ? 'md:order-2' : 'md:order-1'}`}>
        <div
          className="absolute inset-0 bg-cover bg-center transition-transform duration-[1200ms] group-hover:scale-105"
          style={{ backgroundImage: `url(${data.image})` }}
        />
      </div>
    </div>
  );
}

function Mission() {
  const [ref, inView] = useReveal();
  return (
    <section className="relative bg-[#f8f6f2] py-28 md:py-36 overflow-hidden">
      <span className="watermark absolute top-6 left-1/2 -translate-x-1/2 text-[130px] md:text-[220px] whitespace-nowrap">
        Mission
      </span>
      <div ref={ref} className={`relative max-w-[860px] mx-auto px-6 text-center reveal ${inView ? 'in-view' : ''}`}>
        <p className="font-sans-ah tracking-nav text-[13px] text-[#3d7ca6] font-500 mb-8">OUR MISSION.</p>
        <blockquote className="font-serif-ah text-[#2f2f2f] text-[28px] md:text-[38px] font-300 leading-snug italic">
          &ldquo;To be the voice for a prosperous business community that fosters pride and collaboration within Almaguin
          Highlands and to encourage economic growth on behalf of its members and the communities it represents.&rdquo;
        </blockquote>
        <p className="font-sans-ah tracking-wide-ah text-[12px] text-[#888] font-500 uppercase mt-10">
          Almaguin Highlands Chamber of Commerce
        </p>
        <Link
          to="/about"
          className="inline-block btn-ah-outline-dark font-sans-ah text-[13px] px-9 py-4 rounded-sm mt-8"
        >
          ABOUT THE CHAMBER
        </Link>
      </div>
    </section>
  );
}

export default function Explore() {
  return (
    <>
      <Intro />
      <div>
        {EXPLORE_SECTIONS.map((s) => (
          <ExploreBlock key={s.id} data={s} />
        ))}
      </div>
      <Mission />
    </>
  );
}
